import React, { FunctionComponent, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Link } from "react-router";

import FirebaseService from "../../actions/firebase/FirebaseActions";
import { MailSettings } from "../../models/userData/settings/MailSettings";
import { useAuthUser } from "../../hooks/useAuthUser";
import { EmailVerifiedNotification } from "../common/EmailVerifiedNotification";
import { WysiwygFormWidget } from "../settings/widget/WysiwygFormWidget";
import { trackEvent } from "../../helper/analytics";

interface Props {
  userId: string;
  emailSettings?: MailSettings;
  waitlistEnabled: boolean;
}

type MailTab = "intern" | "kunde" | "warteliste";

type TemplateKeys = { subject: keyof MailSettings; template: keyof MailSettings };

const TAB_FIELDS: Record<MailTab, TemplateKeys> = {
  intern: { subject: "confirmationSubject", template: "confirmationTemplate" },
  kunde: { subject: "customerConfirmationSubject", template: "customerConfirmationTemplate" },
  warteliste: { subject: "waitlistCustomerConfirmationSubject", template: "waitlistCustomerConfirmationTemplate" }
};

const PLACEHOLDER = "{{{ formData }}}";

/** Zeichenzahl der Vorlage ohne HTML-Auszeichnung. */
const plainTextLength = (html: string): number => html.replace(/<[^>]*>/g, "").trim().length;

const EmailSettings: FunctionComponent<Props> = ({ userId, emailSettings, waitlistEnabled }: Props) => {
  const { user } = useAuthUser();
  const [settings, setSettings] = useState<MailSettings | undefined>(emailSettings);
  const [tab, setTab] = useState<MailTab>("intern");

  useEffect(() => setSettings(emailSettings), [emailSettings]);

  if (!settings) {
    return <p>E-Mail-Einstellungen konnten nicht geladen werden. Bitte wenden Sie sich an den Support.</p>;
  }

  if (!user?.emailVerified) {
    return <EmailVerifiedNotification user={user} />;
  }

  const update = (patch: Partial<MailSettings>): void => setSettings({ ...settings, ...patch });

  const save = (): void => {
    FirebaseService.updateEmailSettings(userId, settings)
      .then(() => toast.success("Die Einstellungen wurden erfolgreich gespeichert."))
      .catch((error) => toast.error(`Fehler: ${error}`))
      .finally(() => trackEvent("update-email-settings"));
  };

  const tabs: { id: MailTab; label: string }[] = [
    { id: "intern", label: "An Sie" },
    { id: "kunde", label: "An den Kunden" },
    ...(waitlistEnabled ? [{ id: "warteliste" as MailTab, label: "Bei Warteliste" }] : [])
  ];
  const activeTab = tabs.some((item) => item.id === tab) ? tab : "intern";
  const fields = TAB_FIELDS[activeTab];
  const subject = settings[fields.subject] as string | undefined;
  const template = settings[fields.template] as string | undefined;

  const setSubject = (value: string): void => update({ [fields.subject]: value });
  const setTemplate = (value: string): void => update({ [fields.template]: value });

  return (
    <div className="mail-settings">
      <div className="mail-settings__main">
        <section className="panel">
          <div className="panel__head">
            <h2>Versand</h2>
            <div className="mail-toggle-row">
              <span>E-Mail-Versand aktiv</span>
              <button
                type="button"
                role="switch"
                aria-checked={!!settings.mailActive}
                aria-label="E-Mail-Versand aktiv"
                className={`toggle${settings.mailActive ? " is-on" : ""}`}
                onClick={() => update({ mailActive: !settings.mailActive })}>
                <span className="toggle__knob" />
              </button>
            </div>
          </div>
          <div className="panel__body">
            <label className="field-inspector__label">
              <span>Empfänger-Adresse</span>
              <input
                type="email"
                className="form-control"
                value={settings.confirmationEmailAddress || ""}
                onChange={(event) => update({ confirmationEmailAddress: event.target.value })}
              />
              <small className="form-text">An diese Adresse gehen die Bestätigungen der eingehenden Anmeldungen.</small>
            </label>
          </div>
        </section>

        <div className="mail-tabs">
          <div className="pill-tabs" role="tablist">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                role="tab"
                aria-selected={activeTab === item.id}
                className={`pill-tab${activeTab === item.id ? " is-active" : ""}`}
                onClick={() => setTab(item.id)}>
                {item.label}
              </button>
            ))}
          </div>
          <span className="mail-tabs__hint">
            Platzhalter: <code className="mail-placeholder">{PLACEHOLDER}</code>
          </span>
        </div>

        <section className="panel">
          <div className="panel__body field-inspector__body">
            {activeTab === "warteliste" && (
              <small className="form-text">
                Geht statt der normalen Kunden-E-Mail an Familien, die sich auf die Warteliste setzen. Leer lassen, um
                die normale Kunden-E-Mail zu verwenden.
              </small>
            )}
            <label className="field-inspector__label">
              <span>Betreff</span>
              <input
                className="form-control"
                value={subject || ""}
                onChange={(event) => setSubject(event.target.value)}
              />
            </label>

            <div className="field-inspector__label">
              <span>Nachricht</span>
              <div className="mail-editor">
                <WysiwygFormWidget
                  key={activeTab}
                  id={`mail-template-${activeTab}`}
                  label={"Nachricht"}
                  value={template}
                  onChange={setTemplate}
                />
              </div>
              <small className="form-text">{plainTextLength(template || "")} Zeichen</small>
            </div>
          </div>
        </section>
      </div>

      <aside className="mail-settings__aside">
        <section className="panel">
          <div className="panel__head panel__head--tinted">
            <h2>Wann wird gesendet?</h2>
          </div>
          <div className="panel__body">
            <div className="mail-hint">
              <div className="mail-hint__bar" />
              <div>
                <div className="mail-hint__title">Nach Anmeldung</div>
                <div className="mail-hint__text">Bestätigung an Sie und an die Familie, sofort.</div>
              </div>
            </div>
            <div className="mail-hint">
              <div className="mail-hint__bar" />
              <div>
                <div className="mail-hint__title">Formulardaten</div>
                <div className="mail-hint__text">Der Platzhalter wird durch die eingegebenen Angaben ersetzt.</div>
              </div>
            </div>
            {waitlistEnabled && (
              <div className="mail-hint">
                <div className="mail-hint__bar" />
                <div>
                  <div className="mail-hint__title">Bei Warteliste</div>
                  <div className="mail-hint__text">
                    Sie erhalten die E-Mail mit «[Warteliste]» im Betreff, die Familie die Wartelisten-Vorlage.
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className="promo-card">
          <h3>Spielgruppe Plus</h3>
          <p>Erweiterte Gestaltung der E-Mails, eigenes Logo und HTML-Vorlagen.</p>
          <Link to={`/user/${userId}/plus`}>Mehr erfahren →</Link>
        </section>

        <button type="button" className="btn btn-primary" onClick={save}>
          Speichern
        </button>
      </aside>
    </div>
  );
};

export default EmailSettings;
